import { operation } from "@/lib/api-client";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState, useRef } from "react";
import { useAuth } from "@/hooks/use-auth";
import { AppLayout } from "@/components/app-layout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Newspaper,
  RefreshCw,
  Plus,
  Trash2,
  ThumbsUp,
  ThumbsDown,
  ExternalLink,
  Sparkles,
  Settings2,
  MessageSquarePlus,
  Loader2,
  Mail,
  ListFilter,
  ArrowLeft,
} from "lucide-react";
import { toast } from "sonner";
import {
  listSubscriptions,
  createSubscription,
  updateSubscription,
  deleteSubscription,
  seedSubscriptionsFromProjects,
  getSourcePreferences,
  upsertSourcePreferences,
  listStories,
  voteStory,
  deleteStory,
  refreshFeed,
  getLatestRun,
  sendTestDigest,
} from "@/features/feed/feed.functions";
import { unwrap } from "@/lib/server-fn";

export const Route = createFileRoute("/feed")({
  component: FeedPage,
  head: () => ({
    meta: [
      { title: "Feed — AskBell" },
      {
        name: "description",
        content:
          "A personalized daily feed of news on the clients and industries you cover, synthesized by AskBell.",
      },
    ],
  }),
});

const SIGNAL_OPTIONS = [
  { value: "competitor", label: "Competitor moves" },
  { value: "reputation", label: "Brand reputation" },
  { value: "m_and_a", label: "M&A / deals" },
  { value: "regulatory", label: "Regulatory" },
  { value: "exec_changes", label: "Exec changes" },
  { value: "earnings", label: "Earnings / financials" },
  { value: "product", label: "Product launches" },
];

const KIND_OPTIONS = [
  { value: "client", label: "Client" },
  { value: "industry", label: "Industry" },
  { value: "topic", label: "Topic" },
];

type Sub = {
  id: string;
  label: string;
  kind: string;
  keywords: string[];
  signal_types: string[];
  active: boolean;
};

type Story = {
  id: string;
  subscription_id: string | null;
  headline: string;
  summary: string;
  why_it_matters: string | null;
  signal_type: string | null;
  relevance: number;
  source_domains: string[];
  citations: Array<{ url: string; title?: string }> | null;
  created_at: string;
};

type Prefs = {
  domain_allowlist: string[];
  domain_blocklist: string[];
  recency: "day" | "week" | "month";
};

type LatestRun = {
  id: string;
  status: string;
  trigger: string;
  started_at: string;
  finished_at: string | null;
  story_count: number;
  subscription_count: number;
  error: string | null;
} | null;

function FeedPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [subs, setSubs] = useState<Sub[]>([]);
  const [stories, setStories] = useState<Story[]>([]);
  const [feedback, setFeedback] = useState<Record<string, "up" | "down">>({});
  const [prefs, setPrefs] = useState<Prefs>({
    domain_allowlist: [],
    domain_blocklist: [],
    recency: "day",
  });
  const [latestRun, setLatestRun] = useState<LatestRun>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loadAttempt, setLoadAttempt] = useState(0);
  const mounted = useRef(false);
  const loadVersion = useRef(0);
  const refreshPending = useRef(false);
  const pendingMutations = useRef(new Set<string>());
  const [pendingIds, setPendingIds] = useState<Set<string>>(new Set());
  const [mobileDetail, setMobileDetail] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const refreshRequest=useRef<string|null>(null);
  const [sendingDigest, setSendingDigest] = useState(false);
  const [digestEnabled, setDigestEnabled] = useState(false);
  const [activeSubId, setActiveSubId] = useState<string | null>(null);
  const [activeStoryId, setActiveStoryId] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [prefsOpen, setPrefsOpen] = useState(false);
  const [subsManagerOpen, setSubsManagerOpen] = useState(false);

  const loadAll = async () => {
    const version = ++loadVersion.current;
    const [sRaw, pRaw, stRaw, rRaw, digest] = await Promise.all([
      listSubscriptions(),
      getSourcePreferences(),
      listStories({ data: { limit: 100 } }),
      getLatestRun(),
      operation<{}, {enabled:boolean}>("digest_status", {}),
    ]);
    if (!mounted.current || version !== loadVersion.current) return;
    setDigestEnabled(digest.enabled);
    const s = unwrap<{ subscriptions?: Sub[] }>(sRaw);
    const p = unwrap<{ preferences?: Partial<Prefs> }>(pRaw);
    const st = unwrap<{ stories?: Story[]; feedback?: Record<string, "up" | "down"> }>(stRaw);
    const r = unwrap<{ run?: LatestRun }>(rRaw);
    setSubs(s.subscriptions ?? []);
    setPrefs({
      domain_allowlist: p.preferences?.domain_allowlist ?? [],
      domain_blocklist: p.preferences?.domain_blocklist ?? [],
      recency: (p.preferences?.recency as Prefs["recency"]) ?? "day",
    });
    setStories(st.stories ?? []);
    setFeedback(st.feedback ?? {});
    setLatestRun(r.run ?? null);
    setLoadError(null);
  };

  useEffect(() => {
    if (!user) return;
    mounted.current = true;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setLoadError(null);
      try {
        // Seed from projects on first visit (server is idempotent)
        const seedRaw = await seedSubscriptionsFromProjects();
        unwrap<{ seeded?: number; skipped?: boolean }>(seedRaw);
        if (cancelled) return;
        await loadAll();
      } catch (e) {
        if (!cancelled) setLoadError(e instanceof Error ? e.message : "Couldn't load your feed");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      mounted.current = false;
      loadVersion.current++;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, loadAttempt]);

  useEffect(()=>{
    if(latestRun?.status!=="running" || loadError || loading)return;
    let active=true,inFlight=false;
    const timer=setInterval(async()=>{if(inFlight)return;inFlight=true;try{const r=unwrap<{run:LatestRun}>(await getLatestRun());if(!active)return;if(r.run?.status!=="running"){await loadAll();if(!active)return;if(r.run?.error)toast.error(r.run.error);else toast.success(`Feed updated: ${r.run?.story_count??0} new stories.`);}else setLatestRun(r.run);}catch(e){if(active)setLoadError(e instanceof Error?e.message:"Could not check feed research");}finally{inFlight=false;}},3000);
    return()=>{active=false;clearInterval(timer);};
  },[latestRun?.id,latestRun?.status,loadError,loading]);

  const visibleStories = useMemo(() => {
    if (!activeSubId) return stories;
    return stories.filter((s) => s.subscription_id === activeSubId);
  }, [stories, activeSubId]);

  const activeStory = useMemo(
    () => visibleStories.find((s) => s.id === activeStoryId) ?? visibleStories[0] ?? null,
    [visibleStories, activeStoryId]
  );

  const handleRefresh = async () => {
    if (refreshPending.current || loading || loadError || latestRun?.status === "running") return;
    refreshPending.current = true;
    setRefreshing(true);
    try {
      refreshRequest.current??=crypto.randomUUID();
      const raw = await refreshFeed({requestId:refreshRequest.current});
      const res = unwrap<{
        ok?: boolean;
        error?: string | null;
        stories_added?: number;
        queued?: boolean;
      }>(raw);
      if (!res.ok) {
        toast.error(res.error ?? "Refresh failed");
      } else if(res.queued){
        toast.success("Feed research started. New stories will appear when it finishes.");
      } else {
        const added = res.stories_added ?? 0;
        toast.success(
          added > 0
            ? `Added ${added} new ${added === 1 ? "story" : "stories"}`
            : "No new stories since last refresh"
        );
      }
      await loadAll();
      refreshRequest.current=null;
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : "Couldn't confirm the refresh. Retry loading to check its status.");
    } finally {
      refreshPending.current = false;
      setRefreshing(false);
    }
  };

  const beginMutation = (id: string) => {
    if (pendingMutations.current.has(id)) return false;
    pendingMutations.current.add(id);
    setPendingIds(new Set(pendingMutations.current));
    return true;
  };
  const endMutation = (id: string) => {
    pendingMutations.current.delete(id);
    setPendingIds(new Set(pendingMutations.current));
  };

  const handleSendTestDigest = async () => {
    setSendingDigest(true);
    try {
      const started = await operation<{requestId:string},{jobId:string}>("sendTestDigest", {requestId:crypto.randomUUID()});
      const deadline=Date.now()+120_000;
      while(Date.now()<deadline){
        const state=await operation<{jobId:string},{status:string;error?:string}>("digest_delivery_state", {jobId:started.jobId});
        if(state.status==="succeeded"){toast.success("Digest accepted for delivery — check your inbox.");return;}
        if(["failed","partial","cancelled"].includes(state.status))throw new Error(state.error??"Digest delivery stopped.");
        await new Promise(resolve=>setTimeout(resolve,2000));
      }
      toast.info("Digest delivery is still pending. Avoid sending another test until its status is confirmed.");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to send digest");
    } finally {
      setSendingDigest(false);
    }
  };

  const handleVote = async (storyId: string, vote: "up" | "down") => {
    if (!beginMutation(storyId)) return;
    const current = feedback[storyId];
    const next = current === vote ? null : vote;
    setFeedback((m) => {
      const copy = { ...m };
      if (next === null) delete copy[storyId];
      else copy[storyId] = next;
      return copy;
    });
    try {
      const raw = await voteStory({ data: { story_id: storyId, vote: next } });
      unwrap<{ ok?: boolean }>(raw);
    } catch {
      setFeedback((m) => {
        const copy = { ...m };
        if (current) copy[storyId] = current;
        else delete copy[storyId];
        return copy;
      });
      toast.error("Couldn't save your vote");
    } finally { endMutation(storyId); }
  };

  const handleDeleteStory = async (storyId: string) => {
    if (!beginMutation(storyId)) return;
    try {
      const raw = await deleteStory({ data: { id: storyId } });
      unwrap<{ ok?: boolean }>(raw);
      setStories((xs) => xs.filter((s) => s.id !== storyId));
      if (activeStoryId === storyId) setActiveStoryId(null);
      setMobileDetail(false);
      toast.success("Story removed");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Couldn't remove story");
    } finally { endMutation(storyId); }
  };
  const handleDeleteSub = async (id: string) => {
    if (!beginMutation(id)) return;
    try {
      const raw = await deleteSubscription({ data: { id } });
      unwrap<{ ok?: boolean }>(raw);
      setSubs((xs) => xs.filter((s) => s.id !== id));
      if (activeSubId === id) setActiveSubId(null);
    } catch {
      toast.error("Couldn't remove subscription");
    } finally { endMutation(id); }
  };

  const handleToggleActive = async (sub: Sub) => {
    if (!beginMutation(sub.id)) return;
    const next = !sub.active;
    try {
      const raw = await updateSubscription({ data: { id: sub.id, active: next } });
      unwrap<{ subscription?: Sub }>(raw);
      setSubs((xs) => xs.map((s) => (s.id === sub.id ? { ...s, active: next } : s)));
    } catch {
      toast.error("Couldn't update subscription");
    } finally { endMutation(sub.id); }
  };

  const askAboutStory = (story: Story) => {
    const lines = [
      `I want to discuss this story from my feed:`,
      ``,
      `**${story.headline}**`,
      story.summary ? `\n${story.summary}` : "",
      story.why_it_matters ? `\nWhy it matters: ${story.why_it_matters}` : "",
      story.citations && story.citations.length > 0
        ? `\nSources: ${story.citations.slice(0, 3).map((c) => c.url).join(", ")}`
        : "",
      ``,
      `What's your take, and what should I do about it?`,
    ]
      .filter(Boolean)
      .join("\n");
    navigate({
      to: "/chat",
      search: { prefill: lines } as any,
    });
  };

  if (loadError) return (
    <AppLayout>
      <div className="p-6 max-w-xl mx-auto w-full">
        <Card><CardContent className="p-6" role="alert">
          <Newspaper className="h-6 w-6 text-primary mb-3" />
          <h1 className="font-bold">Couldn't load your feed</h1>
          <p className="text-ui-base text-muted-foreground mt-2">{loadError}</p>
          <p className="text-ui-base text-muted-foreground mt-2">Retry to load your saved stories and check the latest refresh status.</p>
          <Button className="mt-4" onClick={() => setLoadAttempt((n) => n + 1)}>Retry loading</Button>
        </CardContent></Card>
      </div>
    </AppLayout>
  );

  return (
    <AppLayout>
      <div className="flex h-full flex-col">
        {/* Header */}
        <div className="border-b border-border px-4 sm:px-6 py-4 flex flex-col lg:flex-row lg:items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <Newspaper className="h-5 w-5 text-primary shrink-0" />
            <h1 className="text-ui-xl font-bold tracking-tight truncate">Feed</h1>
            <Badge variant="outline" className="text-ui-xs uppercase tracking-wider">
              Beta
            </Badge>
          </div>
          <fieldset disabled={loading} className="flex flex-wrap items-center gap-2 min-w-0">
            <Button
              onClick={() => setSubsManagerOpen(true)}
              size="sm"
              variant="outline"
              title="Manage your subscriptions"
              data-tour-feed-subscriptions=""
            >
              <ListFilter className="h-4 w-4" />
              Subscriptions
              <Badge variant="secondary" className="ml-1 px-1.5 text-ui-xs">
                {subs.length}
              </Badge>
            </Button>
            <PrefsDialog
              open={prefsOpen}
              onOpenChange={setPrefsOpen}
              prefs={prefs}
              onSaved={(p) => setPrefs(p)}
            />
            <Button
              onClick={handleSendTestDigest}
              disabled={loading || sendingDigest || refreshing || !digestEnabled || !latestRun || latestRun.status === "running"}
              size="sm"
              variant="outline"
              title="Email yourself a digest of the most recent run"
              data-tour-feed-digest=""
            >
              {sendingDigest ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Mail className="h-4 w-4" />
              )}
              {digestEnabled ? "Send test digest" : "Email delivery disabled"}
            </Button>
            <Button
              onClick={handleRefresh}
              disabled={loading || refreshing || latestRun?.status === "running"}
              size="sm"
              data-tour-feed-refresh=""
            >
              {refreshing ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4" />
              )}
              Refresh now
            </Button>
          </fieldset>
        </div>

        {latestRun && <LastRunBanner run={latestRun} />}

        {/* Filter chip bar — replaces the old left sidebar */}
        <div className="border-b border-border px-4 py-2 flex items-center gap-2 overflow-x-auto">
          <button
            onClick={() => setActiveSubId(null)}
            aria-pressed={!activeSubId}
            className={`shrink-0 px-3 py-1 rounded-full text-ui-sm transition-colors ${
              !activeSubId
                ? "bg-primary text-primary-foreground"
                : "bg-secondary/50 text-muted-foreground hover:bg-secondary"
            }`}
          >
            All
            <span className="ml-1.5 opacity-70">{stories.length}</span>
          </button>
          {!loading && subs.length === 0 ? (
            <button
              onClick={() => setSubsManagerOpen(true)}
              className="shrink-0 px-3 py-1 rounded-full text-ui-sm border border-dashed border-border text-muted-foreground hover:bg-secondary/50"
            >
              <Plus className="inline h-3 w-3 mr-1" />
              Add a subscription
            </button>
          ) : (
            subs.map((s) => {
              const count = stories.filter((st) => st.subscription_id === s.id).length;
              const isActive = activeSubId === s.id;
              return (
                <button
                  key={s.id}
                  onClick={() => setActiveSubId(s.id)}
                  aria-pressed={isActive}
                  title={`${s.label} · ${s.kind}${s.active ? "" : " · paused"}`}
                  className={`shrink-0 px-3 py-1 rounded-full text-ui-sm transition-colors flex items-center gap-1.5 ${
                    isActive
                      ? "bg-primary text-primary-foreground"
                      : s.active
                        ? "bg-secondary/50 text-foreground hover:bg-secondary"
                        : "bg-secondary/30 text-muted-foreground line-through hover:bg-secondary/50"
                  }`}
                >
                  <span className="max-w-[140px] truncate">{s.label}</span>
                  <span className="opacity-70">{count}</span>
                </button>
              );
            })
          )}
        </div>

        <div className="grid flex-1 min-h-0 grid-cols-1 md:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)]">
          {/* Timeline */}
          <div className={`border-r border-border flex-col min-h-0 ${mobileDetail ? "hidden md:flex" : "flex"}`}>
            <div className="px-4 py-3 text-ui-sm uppercase tracking-wider text-muted-foreground">
              {activeSubId
                ? subs.find((s) => s.id === activeSubId)?.label ?? "Stories"
                : "All stories"}
            </div>
            <ScrollArea className="flex-1" data-tour-feed-timeline="">
              <div className="px-3 pb-4 flex flex-col gap-2">
                {loading && (
                  <p className="px-2 py-6 text-ui-base text-muted-foreground">Loading…</p>
                )}
                {!loading && visibleStories.length === 0 && (
                  <EmptyTimeline onRefresh={handleRefresh} refreshing={refreshing || latestRun?.status === "running"} />
                )}
                {visibleStories.map((s) => {
                  const isActive = activeStory?.id === s.id;
                  return (
                    <div
                      key={s.id}
                      className={`group relative rounded-ui-md border transition-colors ${
                        isActive
                          ? "border-primary/50 bg-primary/5"
                          : "border-border hover:bg-secondary/40"
                      }`}
                    >
                      <button
                        onClick={() => { setActiveStoryId(s.id); setMobileDetail(true); }}
                        className="text-left w-full p-3 pr-9"
                      >
                        <h3 className="text-ui-base font-bold leading-snug line-clamp-2 text-foreground">
                          {s.headline}
                        </h3>
                        <p className="mt-1 text-ui-sm text-muted-foreground line-clamp-2">
                          {s.summary}
                        </p>
                        <div className="mt-2 flex items-center gap-2 text-ui-xs text-muted-foreground">
                          {s.signal_type && (
                            <Badge variant="outline" className="text-ui-xs capitalize">
                              {s.signal_type.replace(/_/g, " ")}
                            </Badge>
                          )}
                          {s.citations && s.citations.length > 0 && (
                            <Badge variant="outline" className="text-ui-xs gap-1">
                              <ExternalLink className="h-2.5 w-2.5" />
                              {s.citations.length}{" "}
                              {s.citations.length === 1 ? "source" : "sources"}
                            </Badge>
                          )}
                          {s.source_domains.slice(0, 2).map((d) => (
                            <span key={d} className="truncate">
                              {d}
                            </span>
                          ))}
                          <span className="ml-auto">
                            {new Date(s.created_at).toLocaleDateString()}
                          </span>
                        </div>
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteStory(s.id);
                        }}
                        disabled={pendingIds.has(s.id)}
                        className="absolute top-2 right-2 rounded p-1 text-muted-foreground md:opacity-0 group-hover:opacity-100 focus-visible:opacity-100 hover:bg-destructive/20 hover:text-destructive transition-opacity disabled:opacity-50"
                        aria-label="Remove story"
                        title="Remove — won't appear again"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  );
                })}
              </div>
            </ScrollArea>
          </div>

          {/* Preview */}
          <div className={`flex-col min-h-0 ${mobileDetail ? "flex" : "hidden md:flex"}`}>
            <Button variant="ghost" size="sm" className="md:hidden self-start m-2" onClick={() => setMobileDetail(false)}><ArrowLeft className="h-4 w-4" />Back to stories</Button>
            {activeStory ? (
              <StoryPreview
                story={activeStory}
                pending={pendingIds.has(activeStory.id)}
                vote={feedback[activeStory.id] ?? null}
                onVote={(v) => handleVote(activeStory.id, v)}
                onAsk={() => askAboutStory(activeStory)}
                onDelete={() => handleDeleteStory(activeStory.id)}
              />
            ) : (
              <div className="flex flex-1 items-center justify-center text-ui-base text-muted-foreground p-6 text-center">
                Pick a story to see the full summary, sources, and ask AskBell about it.
              </div>
            )}
          </div>
        </div>
      </div>

      <SubsManagerDialog
        open={subsManagerOpen}
        onOpenChange={setSubsManagerOpen}
        subs={subs}
        stories={stories}
        pendingIds={pendingIds}
        addOpen={addOpen}
        onAddOpenChange={setAddOpen}
        onCreated={(s) => setSubs((xs) => [...xs, s])}
        onToggleActive={handleToggleActive}
        onDelete={handleDeleteSub}
      />
    </AppLayout>
  );
}

function LastRunBanner({ run }: { run: NonNullable<LatestRun> }) {
  const finished = run.finished_at ? new Date(run.finished_at) : null;
  const started = new Date(run.started_at);
  const when = finished ?? started;
  const timeStr = when.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });

  if (run.status === "running") {
    return (
      <div className="border-b border-border bg-secondary/40 px-6 py-2 text-ui-sm text-muted-foreground flex items-center gap-2">
        <Loader2 className="h-3.5 w-3.5 animate-spin text-primary" />
        Refresh in progress — started {timeStr}.
      </div>
    );
  }

  if (run.status === "failed") {
    return (
      <div className="border-b border-destructive/40 bg-destructive/10 px-6 py-2 text-ui-sm text-destructive flex items-start gap-2">
        <span className="font-bold shrink-0">Last refresh failed · {timeStr}</span>
        {run.error && (
          <span className="text-destructive/90 truncate" title={run.error}>
            — {run.error}
          </span>
        )}
      </div>
    );
  }

  // succeeded
  return (
    <div className="border-b border-border bg-secondary/30 px-6 py-2 text-ui-sm text-muted-foreground flex items-center gap-2">
      <span>
        Last refresh {timeStr} ·{" "}
        <span className="text-foreground font-bold">
          {run.story_count} {run.story_count === 1 ? "story" : "stories"} added
        </span>{" "}
        across {run.subscription_count}{" "}
        {run.subscription_count === 1 ? "subscription" : "subscriptions"}
        {run.trigger === "cron" ? " · auto" : ""}
      </span>
      {run.error && (
        <span className="text-destructive/90 ml-2 truncate" title={run.error}>
          partial errors: {run.error}
        </span>
      )}
    </div>
  );
}

function EmptyTimeline({
  onRefresh,
  refreshing,
}: {
  onRefresh: () => void;
  refreshing: boolean;
}) {
  return (
    <Card className="border-dashed">
      <CardContent className="p-6 text-center">
        <Sparkles className="h-6 w-6 text-primary mx-auto mb-2" />
        <h3 className="text-ui-base font-bold">No stories yet</h3>
        <p className="mt-1 text-ui-sm text-muted-foreground">
          Add a few subscriptions, then run a refresh to pull in the latest news.
        </p>
        <Button onClick={onRefresh} disabled={refreshing} size="sm" className="mt-4">
          {refreshing ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <RefreshCw className="h-4 w-4" />
          )}
          Refresh now
        </Button>
      </CardContent>
    </Card>
  );
}

function StoryPreview({
  story,
  pending,
  vote,
  onVote,
  onAsk,
  onDelete,
}: {
  story: Story;
  pending: boolean;
  vote: "up" | "down" | null;
  onVote: (v: "up" | "down") => void;
  onAsk: () => void;
  onDelete: () => void;
}) {
  return (
    <div className="flex flex-1 flex-col min-h-0">
      <div className="px-6 py-5 border-b border-border">
        <div className="flex items-center gap-2 text-ui-sm text-muted-foreground">
          {story.signal_type && (
            <Badge variant="outline" className="text-ui-xs capitalize">
              {story.signal_type.replace(/_/g, " ")}
            </Badge>
          )}
          <span>{new Date(story.created_at).toLocaleString()}</span>
        </div>
        <h2 className="mt-2 text-ui-xl font-bold tracking-tight leading-tight text-foreground">
          {story.headline}
        </h2>
      </div>
      <ScrollArea className="flex-1">
        <div className="px-6 py-5 space-y-5">
          <p className="text-ui-base leading-relaxed text-foreground whitespace-pre-wrap">
            {story.summary}
          </p>
          {story.why_it_matters && (
            <div className="rounded-ui-md border border-primary/20 bg-primary/5 p-3">
              <div className="text-ui-xs uppercase tracking-wider text-primary font-bold">
                Why it matters
              </div>
              <p className="mt-1 text-ui-base text-foreground">{story.why_it_matters}</p>
            </div>
          )}
          {story.citations && story.citations.length > 0 && (
            <div>
              <div className="text-ui-xs uppercase tracking-wider text-muted-foreground font-bold mb-2">
                Sources
              </div>
              <ul className="space-y-1.5">
                {story.citations.map((c, i) => (
                  <li key={i}>
                    <a
                      href={c.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-start gap-1.5 text-ui-sm text-primary hover:underline break-all"
                    >
                      <ExternalLink className="h-3 w-3 mt-0.5 shrink-0" />
                      <span>{c.title || c.url}</span>
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </ScrollArea>
      <div className="border-t border-border px-4 sm:px-6 py-3 flex flex-wrap items-center gap-2">
        <Button
          variant={vote === "up" ? "default" : "outline"}
          size="sm"
          onClick={() => onVote("up")}
          disabled={pending}
          aria-pressed={vote === "up"}
        >
          <ThumbsUp className="h-3.5 w-3.5" />
          Useful
        </Button>
        <Button
          variant={vote === "down" ? "default" : "outline"}
          size="sm"
          onClick={() => onVote("down")}
          disabled={pending}
          aria-pressed={vote === "down"}
        >
          <ThumbsDown className="h-3.5 w-3.5" />
          Noise
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={onDelete}
          disabled={pending}
          className="text-muted-foreground hover:text-destructive"
          title="Remove this story from your feed"
        >
          <Trash2 className="h-3.5 w-3.5" />
          Remove
        </Button>
        <Button onClick={onAsk} size="sm" className="ml-auto">
          <MessageSquarePlus className="h-3.5 w-3.5" />
          Ask AskBell about this
        </Button>
      </div>
    </div>
  );
}

function SubDialog({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onCreated: (s: Sub) => void;
}) {
  const [label, setLabel] = useState("");
  const [kind, setKind] = useState<"client" | "industry" | "topic">("client");
  const [keywordsRaw, setKeywordsRaw] = useState("");
  const [signals, setSignals] = useState<string[]>([
    "competitor",
    "reputation",
    "m_and_a",
    "regulatory",
    "exec_changes",
  ]);
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);

  const reset = () => {
    setLabel("");
    setKind("client");
    setKeywordsRaw("");
    setSignals(["competitor", "reputation", "m_and_a", "regulatory", "exec_changes"]);
  };

  const onSubmit = async () => {
    if (!label.trim() || savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    try {
      const keywords = keywordsRaw
        .split(/[\n,]/)
        .map((k) => k.trim())
        .filter(Boolean);
      const raw = await createSubscription({
        data: { label: label.trim(), kind, keywords, signal_types: signals, active: true },
      });
      const res = unwrap<{ subscription?: Sub }>(raw);
      if (res.subscription) onCreated(res.subscription);
      reset();
      onOpenChange(false);
      toast.success("Subscription added");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Couldn't add");
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(value) => { if (!savingRef.current) onOpenChange(value); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add subscription</DialogTitle>
        </DialogHeader>
        <fieldset disabled={saving} className="space-y-4 min-w-0">
          <div>
            <Label htmlFor="subscription-label">Label</Label>
            <Input
              id="subscription-label"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="e.g., Acme Corp"
            />
          </div>
          <div>
            <Label>Kind</Label>
            <Select disabled={saving} value={kind} onValueChange={(v) => setKind(v as typeof kind)}>
              <SelectTrigger aria-label="Subscription kind">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {KIND_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="subscription-keywords">Keywords (comma or newline separated)</Label>
            <Textarea
              id="subscription-keywords"
              value={keywordsRaw}
              onChange={(e) => setKeywordsRaw(e.target.value)}
              rows={2}
              placeholder="acme, acme corp, acme inc"
            />
          </div>
          <div>
            <Label>Signal types</Label>
            <div className="mt-2 flex flex-wrap gap-2">
              {SIGNAL_OPTIONS.map((o) => {
                const on = signals.includes(o.value);
                return (
                  <button
                    key={o.value}
                    type="button"
                    aria-pressed={on}
                    onClick={() =>
                      setSignals((xs) =>
                        on ? xs.filter((x) => x !== o.value) : [...xs, o.value]
                      )
                    }
                    className={`px-2.5 py-1 text-ui-sm rounded-ui-sm border transition-colors ${
                      on
                        ? "bg-primary text-primary-foreground border-primary"
                        : "border-border text-muted-foreground hover:bg-secondary"
                    }`}
                  >
                    {o.label}
                  </button>
                );
              })}
            </div>
          </div>
        </fieldset>
        <DialogFooter>
          <Button disabled={saving} variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={onSubmit} disabled={saving || !label.trim()}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            Add
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SubsManagerDialog({
  open,
  onOpenChange,
  subs,
  stories,
  pendingIds,
  addOpen,
  onAddOpenChange,
  onCreated,
  onToggleActive,
  onDelete,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  subs: Sub[];
  stories: Story[];
  pendingIds: Set<string>;
  addOpen: boolean;
  onAddOpenChange: (v: boolean) => void;
  onCreated: (s: Sub) => void;
  onToggleActive: (s: Sub) => void;
  onDelete: (id: string) => void;
}) {
  const activeCount = subs.filter((s) => s.active).length;
  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Manage subscriptions</DialogTitle>
          </DialogHeader>
          <div className="flex items-center justify-between gap-2 pb-2">
            <p className="text-ui-sm text-muted-foreground">
              {activeCount} of {subs.length} active · hard cap of 8 active per refresh.
            </p>
            <Button size="sm" variant="outline" onClick={() => onAddOpenChange(true)}>
              <Plus className="h-3.5 w-3.5" /> Add subscription
            </Button>
          </div>
          <ScrollArea className="max-h-[60vh]">
            <div className="flex flex-col gap-1 pr-2">
              {subs.length === 0 && (
                <p className="px-2 py-8 text-center text-ui-base text-muted-foreground">
                  No subscriptions yet. Add a client, industry, or topic to start tracking.
                </p>
              )}
              {subs.map((s) => {
                const count = stories.filter((st) => st.subscription_id === s.id).length;
                return (
                  <div
                    key={s.id}
                    className="group flex items-center gap-3 rounded-ui-sm border border-border px-3 py-2 hover:bg-secondary/40"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex min-w-0 items-center gap-2">
                        <span
                          className={`min-w-0 truncate text-ui-base ${
                            s.active
                              ? "text-foreground"
                              : "text-muted-foreground line-through"
                          }`}
                        >
                          {s.label}
                        </span>
                        <Badge variant="outline" className="shrink-0 text-ui-xs capitalize">
                          {s.kind}
                        </Badge>
                        <span className="shrink-0 text-ui-xs text-muted-foreground">
                          {count} {count === 1 ? "story" : "stories"}
                        </span>
                      </div>
                      {s.keywords.length > 0 && (
                        <p className="mt-0.5 truncate text-ui-xs text-muted-foreground">
                          {s.keywords.join(", ")}
                        </p>
                      )}
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      <Switch
                        checked={s.active}
                        disabled={pendingIds.has(s.id)}
                        onCheckedChange={() => onToggleActive(s)}
                        aria-label="Active"
                      />
                      <button
                        disabled={pendingIds.has(s.id)}
                        onClick={() => {
                          if (
                            confirm(
                              `Delete subscription "${s.label}"? Past stories will remain.`
                            )
                          ) {
                            onDelete(s.id);
                          }
                        }}
                        className="rounded p-1 text-muted-foreground hover:bg-destructive/20 hover:text-destructive"
                        aria-label="Delete subscription"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </ScrollArea>
          <DialogFooter>
            <Button onClick={() => onOpenChange(false)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <SubDialog open={addOpen} onOpenChange={onAddOpenChange} onCreated={onCreated} />
    </>
  );
}

function PrefsDialog({
  open,
  onOpenChange,
  prefs,
  onSaved,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  prefs: Prefs;
  onSaved: (p: Prefs) => void;
}) {
  const [allow, setAllow] = useState(prefs.domain_allowlist.join("\n"));
  const [block, setBlock] = useState(prefs.domain_blocklist.join("\n"));
  const [recency, setRecency] = useState<Prefs["recency"]>(prefs.recency);
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const wasOpen = useRef(false);

  useEffect(() => {
    if (open && !wasOpen.current) {
      setAllow(prefs.domain_allowlist.join("\n"));
      setBlock(prefs.domain_blocklist.join("\n"));
      setRecency(prefs.recency);
    }
    wasOpen.current = open;
  }, [open, prefs]);

  const splitLines = (s: string) =>
    s
      .split(/[\n,]/)
      .map((x) => x.trim())
      .filter(Boolean);

  const onSave = async () => {
    if (savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    try {
      const raw = await upsertSourcePreferences({
        data: {
          domain_allowlist: splitLines(allow),
          domain_blocklist: splitLines(block),
          recency,
        },
      });
      const res = unwrap<{
        preferences?: { domain_allowlist?: string[]; domain_blocklist?: string[]; recency?: string };
      }>(raw);
      onSaved({
        domain_allowlist: res.preferences?.domain_allowlist ?? [],
        domain_blocklist: res.preferences?.domain_blocklist ?? [],
        recency: (res.preferences?.recency as Prefs["recency"]) ?? "day",
      });
      onOpenChange(false);
      toast.success("Preferences saved");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Couldn't save");
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(value) => { if (!savingRef.current) onOpenChange(value); }}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          <Settings2 className="h-4 w-4" />
          Sources
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Source preferences</DialogTitle>
        </DialogHeader>
        <fieldset disabled={saving} className="space-y-4 min-w-0">
          <div>
            <Label>Recency window</Label>
            <Select disabled={saving} value={recency} onValueChange={(v) => setRecency(v as Prefs["recency"])}>
              <SelectTrigger aria-label="Recency window">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="day">Last 24 hours</SelectItem>
                <SelectItem value="week">Last 7 days</SelectItem>
                <SelectItem value="month">Last 30 days</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="feed-allow-domains">Allow only these domains (optional)</Label>
            <Textarea
              id="feed-allow-domains"
              value={allow}
              onChange={(e) => setAllow(e.target.value)}
              rows={3}
              placeholder="wsj.com, bloomberg.com, ft.com"
            />
            <p className="mt-1 text-ui-xs text-muted-foreground">
              One per line, or comma-separated. Leave blank for all sources.
            </p>
          </div>
          <div>
            <Label htmlFor="feed-block-domains">Block these domains</Label>
            <Textarea
              id="feed-block-domains"
              value={block}
              onChange={(e) => setBlock(e.target.value)}
              rows={3}
              placeholder="reddit.com, twitter.com"
            />
          </div>
        </fieldset>
        <DialogFooter>
          <Button disabled={saving} variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={onSave} disabled={saving}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
